import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";
import { zonedParts, formatZonedTime, BROWSER_TIMEZONE } from "@/lib/timezones";
import { EmptyState } from "./ui";

const STATUS_COLORS = {
  Scheduled: { bg: "bg-violet-50", border: "border-violet-300", text: "text-violet-800", dot: "bg-violet-500" },
  Completed: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", dot: "bg-emerald-500" },
};

const HOUR_START = 8;
const HOUR_END = 18;
const ROW_HEIGHT = 56;
const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

const legendItems = [
  { status: "Pending", dot: "bg-amber-400" },
  { status: "Contacted", dot: "bg-blue-400" },
  { status: "Scheduled", dot: "bg-violet-500" },
  { status: "Completed", dot: "bg-emerald-500" },
  { status: "Declined", dot: "bg-red-400" },
];

const dateKey = (demo) => {
  const p = zonedParts(demo.start_at, BROWSER_TIMEZONE);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
};

const buildEvents = (requests) =>
  requests
    .filter((r) => r.scheduled_demo && r.scheduled_demo.status === "scheduled")
    .map((r) => ({ request: r, demo: r.scheduled_demo }));

const EventPill = ({ event, onSelect, compact }) => {
  const colors = STATUS_COLORS[event.request.status] || STATUS_COLORS.Scheduled;
  return (
    <button
      type="button"
      onClick={() => onSelect(event.request)}
      data-testid={`calendar-event-${event.request.demo_request_id}`}
      className={`w-full truncate rounded-md border px-1.5 py-0.5 text-left text-[11px] font-semibold ${colors.bg} ${colors.border} ${colors.text} hover:shadow-sm`}
    >
      {compact ? (
        <>{formatZonedTime(event.demo.start_at, BROWSER_TIMEZONE)} {event.request.company}</>
      ) : (
        <>
          <div className="truncate">{formatZonedTime(event.demo.start_at, BROWSER_TIMEZONE)} · {event.demo.duration_minutes}m</div>
          <div className="truncate font-bold">{event.request.company}</div>
        </>
      )}
    </button>
  );
};

export default function DemoCalendar({ requests, onSelect }) {
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(dayjs());

  const events = useMemo(() => buildEvents(requests), [requests]);
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((event) => {
      const key = dateKey(event.demo);
      (map[key] = map[key] || []).push(event);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => new Date(a.demo.start_at) - new Date(b.demo.start_at)));
    return map;
  }, [events]);

  const goPrev = () => setAnchor((a) => a.subtract(1, view === "month" ? "month" : "week"));
  const goNext = () => setAnchor((a) => a.add(1, view === "month" ? "month" : "week"));
  const goToday = () => setAnchor(dayjs());

  const weekStart = anchor.startOf("week");
  const weekDates = Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));

  const monthStart = anchor.startOf("month").startOf("week");
  const monthDates = Array.from({ length: 42 }, (_, i) => monthStart.add(i, "day"));
  const monthWeeks = Array.from({ length: 6 }, (_, i) => monthDates.slice(i * 7, i * 7 + 7));

  const agendaEvents = useMemo(
    () => [...events].sort((a, b) => new Date(a.demo.start_at) - new Date(b.demo.start_at)),
    [events]
  );
  const agendaByDate = useMemo(() => {
    const map = {};
    agendaEvents.forEach((event) => {
      const key = dateKey(event.demo);
      (map[key] = map[key] || []).push(event);
    });
    return map;
  }, [agendaEvents]);
  const agendaDates = useMemo(() => Object.keys(agendaByDate).sort(), [agendaByDate]);

  const label = view === "agenda" ? "All Scheduled Demos" : anchor.format("MMMM YYYY");

  return (
    <div data-testid="demo-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4 dark:border-white/10">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-extrabold tracking-tight text-slate-950 dark:text-white" data-testid="calendar-period-label">{label}</h3>
          {view !== "agenda" && (
            <div className="flex items-center gap-1">
              <button type="button" onClick={goPrev} data-testid="calendar-prev-btn" className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10"><ChevronLeft size={16} /></button>
              <button type="button" onClick={goToday} data-testid="calendar-today-btn" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">Today</button>
              <button type="button" onClick={goNext} data-testid="calendar-next-btn" className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10"><ChevronRight size={16} /></button>
            </div>
          )}
        </div>
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1 dark:bg-white/5" data-testid="calendar-view-toggle">
          {["week", "month", "agenda"].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              data-testid={`calendar-view-${v}`}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold capitalize transition-colors duration-200 ${view === v ? "bg-white text-slate-950 shadow-sm dark:bg-white/10 dark:text-white" : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"}`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {events.length === 0 ? (
        <div className="py-8">
          <EmptyState icon={CalendarClock} title="No scheduled demos yet." message="Demos scheduled from a Demo Request will appear on this calendar." testId="calendar-empty" />
        </div>
      ) : view === "week" ? (
        <div className="mt-4 overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="grid grid-cols-[56px_1fr]">
              <div />
              <div className="grid grid-cols-7">
                {weekDates.map((d) => (
                  <div key={d.format("YYYY-MM-DD")} className="px-1 py-2 text-center text-xs font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    <div>{d.format("ddd")}</div>
                    <div className={`mx-auto mt-1 flex h-6 w-6 items-center justify-center rounded-full text-sm font-bold ${d.isSame(dayjs(), "day") ? "bg-blue-600 text-white" : "text-slate-700 dark:text-slate-300"}`}>
                      {d.format("D")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex">
              <div className="w-14 shrink-0">
                {HOURS.map((h) => (
                  <div key={h} style={{ height: ROW_HEIGHT }} className="border-t border-slate-100 pr-2 text-right text-[11px] text-slate-400 dark:border-white/5 dark:text-slate-500">
                    {dayjs().hour(h).minute(0).format("h A")}
                  </div>
                ))}
              </div>
              <div className="grid flex-1 grid-cols-7">
                {weekDates.map((d) => {
                  const key = d.format("YYYY-MM-DD");
                  return (
                    <div key={key} className="relative border-l border-slate-100 dark:border-white/5" style={{ height: (HOUR_END - HOUR_START) * ROW_HEIGHT }}>
                      {HOURS.map((h) => <div key={h} className="border-t border-slate-100 dark:border-white/5" style={{ height: ROW_HEIGHT }} />)}
                      {(eventsByDate[key] || []).map((event) => {
                        const p = zonedParts(event.demo.start_at, BROWSER_TIMEZONE);
                        const startMinutes = (p.hour - HOUR_START) * 60 + p.minute;
                        const top = Math.max(0, (startMinutes / 60) * ROW_HEIGHT);
                        const height = Math.max(26, (event.demo.duration_minutes / 60) * ROW_HEIGHT);
                        return (
                          <div key={event.request.demo_request_id} className="absolute left-1 right-1" style={{ top, height }}>
                            <EventPill event={event} onSelect={onSelect} />
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : view === "month" ? (
        <div className="mt-4">
          <div className="grid grid-cols-7 text-xs font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            {weekDates.map((d) => <div key={d.format("ddd")} className="px-2 py-2">{d.format("ddd")}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-px rounded-xl border border-slate-100 bg-slate-100 dark:border-white/5 dark:bg-white/5" data-testid="calendar-month-grid">
            {monthWeeks.flat().map((d) => {
              const key = d.format("YYYY-MM-DD");
              const dayEvents = eventsByDate[key] || [];
              const inMonth = d.month() === anchor.month();
              return (
                <div key={key} className={`min-h-[92px] space-y-1 bg-white p-1.5 dark:bg-[#111C33] ${inMonth ? "" : "opacity-40"}`}>
                  <div className={`text-xs font-bold ${d.isSame(dayjs(), "day") ? "text-blue-600 dark:text-blue-400" : "text-slate-500 dark:text-slate-400"}`}>{d.format("D")}</div>
                  {dayEvents.slice(0, 3).map((event) => <EventPill key={event.request.demo_request_id} event={event} onSelect={onSelect} compact />)}
                  {dayEvents.length > 3 && <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500">+{dayEvents.length - 3} more</p>}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-5" data-testid="calendar-agenda-list">
          {agendaDates.map((key) => (
            <div key={key}>
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">{dayjs(key).format("dddd, MMMM D, YYYY")}</p>
              <div className="mt-2 space-y-2">
                {agendaByDate[key].map((event) => (
                  <button
                    key={event.request.demo_request_id}
                    type="button"
                    onClick={() => onSelect(event.request)}
                    data-testid={`agenda-event-${event.request.demo_request_id}`}
                    className="flex w-full items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3 text-left hover:border-blue-200 hover:bg-blue-50/40 dark:border-white/10 dark:hover:border-blue-500/30 dark:hover:bg-blue-500/10"
                  >
                    <div>
                      <p className="text-sm font-bold text-slate-950 dark:text-white">{event.request.company}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{event.request.name}</p>
                    </div>
                    <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                      {formatZonedTime(event.demo.start_at, BROWSER_TIMEZONE)} · {event.demo.duration_minutes} min
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-4 dark:border-white/5" data-testid="calendar-legend">
        {legendItems.map((item) => (
          <span key={item.status} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
            <span className={`h-2 w-2 rounded-full ${item.dot}`} />
            {item.status}
          </span>
        ))}
      </div>
    </div>
  );
}
